import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';

interface Props {
  values: string[];
  options: string[];
  onChange: (next: string[]) => void;
  // Returns the canonical value (e.g. case-folded against existing options).
  onAddOption: (value: string) => Promise<string> | string;
  label?: string;
  placeholder?: string;
}

const MultiSelectWithAddNew: React.FC<Props> = ({
  values,
  options,
  onChange,
  onAddOption,
  placeholder = 'Pick an option…',
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const remove = (v: string) => onChange(values.filter(x => x !== v));

  /**
   * Move a selected value within the list.
   *
   * This order is the order the customer sees in the dropdown, so it
   * should be the shop's -- S, M, L rather than whichever was typed
   * first, and the popular colour near the top.
   */
  const moveTo = (from: number, to: number) => {
    if (from === to || to < 0 || to >= values.length) return;
    const next = [...values];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const selectExisting = (v: string) => {
    if (!v) return;
    if (v === '__add_new__') {
      setAdding(true);
      return;
    }
    if (values.includes(v)) return;
    onChange([...values, v]);
  };

  const commitNew = async () => {
    const cleaned = draft.trim();
    if (!cleaned) {
      setAdding(false);
      return;
    }
    const canonical = (await onAddOption(cleaned)) || cleaned;
    if (!values.some(v => v.toLowerCase() === canonical.toLowerCase())) {
      onChange([...values, canonical]);
    }
    setDraft('');
    setAdding(false);
  };

  // Options visible in the dropdown = master options minus already-selected
  // (case-insensitive so "Red" in `values` hides "red" in `options`).
  const lowerSelected = new Set(values.map(v => v.toLowerCase()));
  const available = options.filter(o => !lowerSelected.has(o.toLowerCase()));

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v, idx) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded px-2 py-0.5 text-xs"
            >
              {/* Arrows rather than dragging: these are small inline chips
                  that wrap across lines, where a drop target is fiddly to
                  hit and impossible to see. */}
              <button
                type="button"
                onClick={() => moveTo(idx, idx - 1)}
                disabled={idx === 0}
                aria-label={`Move ${v} earlier`}
                title="Move earlier"
                className="disabled:opacity-30 hover:text-primary/80"
              >
                <ChevronLeft size={11} />
              </button>
              {v}
              <button
                type="button"
                onClick={() => moveTo(idx, idx + 1)}
                disabled={idx === values.length - 1}
                aria-label={`Move ${v} later`}
                title="Move later"
                className="disabled:opacity-30 hover:text-primary/80"
              >
                <ChevronRight size={11} />
              </button>
              <button
                type="button"
                onClick={() => remove(v)}
                className="hover:text-primary/80"
                title="Remove"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {adding ? (
        <div className="flex gap-1">
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitNew();
              } else if (e.key === 'Escape') {
                setAdding(false);
                setDraft('');
              }
            }}
            placeholder="New value…"
            className="flex-1 border border-background-dark rounded px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={commitNew}
            className="bg-primary text-white rounded px-2 py-1 text-xs"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setDraft('');
            }}
            className="border border-background-dark rounded px-2 py-1 text-xs"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <select
            value=""
            onChange={e => selectExisting(e.target.value)}
            className="flex-1 border border-background-dark rounded px-2 py-1 text-sm bg-white"
          >
            <option value="">{placeholder}</option>
            {available.map(opt => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
            <option value="__add_new__">+ Add new…</option>
          </select>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 border border-background-dark rounded px-2 py-1 text-xs hover:border-primary"
          >
            <Plus size={12} /> New
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiSelectWithAddNew;
